import React, { useEffect, useState } from 'react';
import { getTranslationKey } from '../../modules/utils';
import { Button, Col, UncontrolledTooltip } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { getPaymentInfo } from '../../modules/selectors/paymentInfo';
import { getUser } from '../../modules/selectors';
import { FiEditBtn, Title, useUserHasPayinPaymentMethod } from './common';
import { StripeElements } from '../../components/Stripe/StripeElements';
import {
  StripePaymentElement,
  usePaymentMethodSetup
} from '../../components/Stripe/StripePaymentElement';
import { fetchShopPayinPaymentMethod } from '../../modules/actions';
import { usePaymentPrerequisites } from './use-payment-prerequisites';
import { CardTitle, DisplayCard, Header } from './DisplayCard';
import { SlPaypal } from 'react-icons/sl';
import { VscCreditCard } from 'react-icons/vsc';
import { FaCcMastercard, FaCcVisa } from 'react-icons/fa';
import styled from 'styled-components';
import media from '../../assets/css/media';

const Paragraph = styled.p`
  width: 60%;
  @media all and (max-width: ${media.mobile}) {
    width: 100%;
  }
`;

const ButtonSection = styled.div`
  display: flex;
  @media all and (max-width: ${media.mobile}) {
    flex-direction: column;
    button {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
  }
`;

const BtnWrapper = styled.div`
  width: fit-content;
`;

function PaymentElement({ onCancel }: { onCancel?: () => void }) {
  const user = useSelector(getUser);
  const userHasPaymentMethod = useUserHasPayinPaymentMethod();

  const { confirmSetup, confirming, errorMessage } = usePaymentMethodSetup({
    returnURLFail: `${user.builderDomain}/#/shop/payment-settings`,
    returnURLSuccess: `${user.builderDomain}/#/shop/payment-settings?is_setup=true`,
    metadata: {
      shop_payin: true
    }
  });

  const prerequisites = usePaymentPrerequisites();
  const allPrerequisitesMet = prerequisites.every((p) => p.isMet);
  const failingPrerequisite = prerequisites.find((p) => !p.isMet);

  return (
    <>
      <StripePaymentElement errorMessage={errorMessage} />
      <ButtonSection>
        <BtnWrapper id="payin-payment-method-tooltip">
          <Button
            color="primary"
            className="px-5 mt-3 text-uppercase"
            disabled={!allPrerequisitesMet || confirming}
            type="submit"
            onClick={confirmSetup}
            id="payin-payment-method-tooltip"
          >
            {confirming ? getTranslationKey('buttonLoading') : getTranslationKey('save')}
            {failingPrerequisite && (
              <UncontrolledTooltip
                autohide={false}
                placement="top"
                target="payin-payment-method-tooltip"
              >
                {getTranslationKey(failingPrerequisite.tooltipKey)}
              </UncontrolledTooltip>
            )}
          </Button>
        </BtnWrapper>
        {userHasPaymentMethod && (
          <Button color="secondary" className="px-5 mt-3 ml-2 text-uppercase" onClick={onCancel}>
            {getTranslationKey('messages.cancel')}
          </Button>
        )}
      </ButtonSection>
    </>
  );
}

type PaymentMethodNameProps = {
  type: 'card' | 'paypal' | 'sepa_debit';
  brand: string | null;
};
function PaymentMethod(props: PaymentMethodNameProps) {
  const { type, brand } = props;

  let CardIcon;
  switch (brand) {
    case 'visa':
      CardIcon = FaCcVisa;
      break;
    case 'mastercard':
      CardIcon = FaCcMastercard;
      break;
    default:
      CardIcon = VscCreditCard;
  }

  return (
    <>
      {type === 'card' && (
        <CardTitle>
          <CardIcon size={24} color="#1d1d1d" className="mr-2" />
          <span className="title">
            {brand && <span className="mr-2">{brand.toUpperCase()}</span>}
            <span>Card</span>
          </span>
        </CardTitle>
      )}
      {type === 'paypal' && (
        <CardTitle>
          <SlPaypal size={22} color="#1d1d1d" className="mr-2" />
          <span className="title mr-1">PayPal</span>
        </CardTitle>
      )}
      {type === 'sepa_debit' && (
        <CardTitle>
          <VscCreditCard size={24} color="#1d1d1d" className="mr-2" />
          <span className="title mr-1">SEPA Debit</span>
        </CardTitle>
      )}
    </>
  );
}

function PayinPaymentMethodTabContent() {
  const { shopPayinPaymentMethod } = useSelector(getPaymentInfo);
  const [showSetupForm, setShowSetupForm] = useState(false);
  const userHasPaymentMethod = useUserHasPayinPaymentMethod();
  const user = useSelector(getUser);
  const excludePaymentMethods = ['paypal'];
  if (user?.currency?.toLowerCase() !== 'eur') {
    excludePaymentMethods.push('sepa_debit');
  }

  return (
    <div className="animated fadeIn">
      {userHasPaymentMethod !== true || showSetupForm ? (
        <StripeElements mode="setup" excludePaymentMethods={excludePaymentMethods}>
          <PaymentElement onCancel={() => setShowSetupForm(false)} />
        </StripeElements>
      ) : (
        <DisplayCard className="animated fadeIn">
          <Header>
            <div>
              <PaymentMethod
                type={shopPayinPaymentMethod.type}
                brand={shopPayinPaymentMethod.card?.brand}
              />
            </div>
            <FiEditBtn size={20} onClick={() => setShowSetupForm(true)} />
          </Header>
          <div>
            {shopPayinPaymentMethod.type === 'card' && (
              <div>**** **** **** {shopPayinPaymentMethod.card?.last4}</div>
            )}
            {shopPayinPaymentMethod.type === 'paypal' && (
              <div>{shopPayinPaymentMethod.paypal?.email}</div>
            )}
            {shopPayinPaymentMethod.type === 'sepa_debit' && (
              <div>
                {shopPayinPaymentMethod.sepa_debit?.country} ****************
                {shopPayinPaymentMethod.sepa_debit?.last4}
              </div>
            )}
          </div>
        </DisplayCard>
      )}
    </div>
  );
}

export function PayinPaymentMethodTab() {
  const dispatch = useDispatch();
  const { shopPayinPaymentMethod } = useSelector(getPaymentInfo);

  useEffect(() => {
    dispatch(fetchShopPayinPaymentMethod());
  }, []);

  return (
    <div>
      <Title className="mb-4">
        {getTranslationKey('shop.paymentSettings.payinPaymentMethodTitle')}
      </Title>

      <Paragraph
        className="mb-5"
        dangerouslySetInnerHTML={{
          __html: getTranslationKey('shop.paymentSettings.payinPaymentMethodInfo')
        }}
      />

      <Col md={4} className="p-0 m-0">
        {shopPayinPaymentMethod.loaded && <PayinPaymentMethodTabContent />}
      </Col>
    </div>
  );
}
